
import React, { useState, useEffect } from 'react';
import { ref, onValue, set, push, remove } from '@firebase/database';
import { db } from '../../firebase';
import { Role } from '../../types';
import { KeyRound, Plus, Trash2, Check, X, ShieldCheck, ChevronRight, Info } from 'lucide-react';

const AdminRoles: React.FC = () => {
  const [roles, setRoles] = useState<Role[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    permissions: [] as string[]
  });

  const availablePermissions = [
    { id: 'dashboard', label: 'Panel Özeti' },
    { id: 'orders', label: 'Sipariş Yönetimi' },
    { id: 'reports', label: 'Raporlar ve Analiz' },
    { id: 'customers', label: 'Müşteri Listesi' },
    { id: 'products', label: 'Ürün Yönetimi' },
    { id: 'categories', label: 'Kategori Yönetimi' },
    { id: 'coupons', label: 'Kupon Yönetimi' },
    { id: 'cargo', label: 'Kargo Ayarları' },
    { id: 'support', label: 'Destek Talepleri' },
    { id: 'announcements', label: 'Duyuru ve Popup' },
    { id: 'staff', label: 'Personel Yönetimi' },
    { id: 'roles', label: 'Rol ve Yetki Ayarları' },
  ];

  useEffect(() => {
    onValue(ref(db, 'roles'), (snapshot) => {
      const data = snapshot.val();
      if (data) {
        setRoles(Object.values(data));
      }
    });
  }, []);

  const handleTogglePermission = (id: string) => {
    setFormData(prev => ({
      ...prev,
      permissions: prev.permissions.includes(id) 
        ? prev.permissions.filter(p => p !== id) 
        : [...prev.permissions, id]
    }));
  };

  const handleCreateRole = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name) return;

    const newRef = push(ref(db, 'roles'));
    const roleData: Role = {
      id: newRef.key as string,
      name: formData.name,
      permissions: formData.permissions,
      createdAt: Date.now()
    };

    await set(newRef, roleData);
    setIsModalOpen(false);
    setFormData({ name: '', permissions: [] });
  };

  const deleteRole = (id: string) => {
    if (id === 'admin') return alert("Sistem yöneticisi rolü silinemez.");
    if (window.confirm('Bu rolü silmek istediğinize emin misiniz?')) {
      remove(ref(db, `roles/${id}`));
    }
  };

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-10">
        <div>
          <h1 className="text-3xl font-black text-stone-900">Rol ve Yetki Yönetimi</h1>
          <p className="text-stone-500 font-medium italic mt-1">Sisteme yeni görev tanımları ekleyin.</p>
        </div>
        <button 
          onClick={() => setIsModalOpen(true)}
          className="bg-stone-900 text-white px-8 py-4 rounded-2xl font-bold flex items-center space-x-3 shadow-xl hover:bg-amber-800 transition-all"
        >
          <Plus size={20} />
          <span>Yeni Rol Tanımla</span>
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        <div className="bg-stone-900 text-white p-8 rounded-[3rem] border border-stone-800 shadow-xl relative overflow-hidden">
           <div className="relative z-10">
              <div className="flex items-center justify-between mb-6">
                <div className="w-14 h-14 bg-amber-500 text-stone-900 rounded-2xl flex items-center justify-center shadow-lg">
                  <ShieldCheck size={28} />
                </div>
                <span className="text-[10px] font-black uppercase tracking-widest text-amber-500">SİSTEM ROLÜ</span>
              </div>
              <h3 className="text-2xl font-black mb-2">Yönetici (Admin)</h3>
              <p className="text-stone-400 text-xs mb-8">Bu rol tüm sayfalara tam erişim yetkisine sahiptir.</p>
              <div className="flex items-center text-xs font-bold text-amber-500">
                <Check size={16} className="mr-2" /> Tüm Yetkiler Tanımlı
              </div>
           </div>
        </div>

        {roles.map((role) => (
          <div key={role.id} className="bg-white p-8 rounded-[3rem] border border-stone-100 shadow-sm hover:shadow-xl transition-all flex flex-col group">
            <div className="flex items-center justify-between mb-6">
              <div className="w-14 h-14 bg-stone-50 text-stone-400 group-hover:bg-amber-50 group-hover:text-amber-800 rounded-2xl flex items-center justify-center transition-all">
                <KeyRound size={28} />
              </div>
              <button onClick={() => deleteRole(role.id)} className="p-3 text-stone-300 hover:text-red-500 transition-all opacity-0 group-hover:opacity-100">
                <Trash2 size={20} />
              </button>
            </div>
            <h3 className="text-2xl font-black text-stone-900 mb-2">{role.name}</h3>
            <p className="text-stone-400 text-xs mb-6">{role.permissions.length} Sayfa İzni Tanımlı</p>
            
            <div className="flex-1 overflow-y-auto max-h-32 space-y-2 mb-6 scrollbar-hide">
               {role.permissions.map(p => (
                 <div key={p} className="flex items-center text-[10px] font-black text-stone-500 uppercase tracking-widest">
                    <ChevronRight size={12} className="mr-1 text-amber-500" /> {availablePermissions.find(ap => ap.id === p)?.label}
                 </div>
               ))}
            </div>
          </div>
        ))}
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 z-[200] bg-stone-900/70 backdrop-blur-md flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-2xl rounded-[3rem] shadow-2xl overflow-hidden">
            <div className="p-8 border-b border-stone-50 flex items-center justify-between bg-stone-50/50">
              <h2 className="text-2xl font-black text-stone-900">Yeni Rol Tanımla</h2>
              <button onClick={() => setIsModalOpen(false)}><X size={24} /></button>
            </div>
            <form onSubmit={handleCreateRole} className="p-10 space-y-8">
              <div>
                <label className="block text-[10px] font-black text-stone-400 uppercase tracking-widest mb-3">Rol İsmi</label>
                <input required type="text" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} placeholder="Örn: Teknik Ekip" className="w-full bg-stone-50 border border-stone-100 rounded-2xl py-4 px-6 focus:ring-2 focus:ring-amber-500 outline-none font-bold" />
              </div>
              <div className="grid grid-cols-2 gap-3 max-h-64 overflow-y-auto pr-2">
                {availablePermissions.map((perm) => (
                  <button key={perm.id} type="button" onClick={() => handleTogglePermission(perm.id)} className={`flex items-center justify-between p-4 rounded-xl border transition-all ${formData.permissions.includes(perm.id) ? 'bg-amber-50 border-amber-500 text-amber-900' : 'bg-white border-stone-100 text-stone-500'}`}>
                    <span className="text-xs font-bold">{perm.label}</span>
                    {formData.permissions.includes(perm.id) && <Check size={16} />}
                  </button>
                ))}
              </div>
              <button type="submit" className="w-full bg-stone-900 text-white py-5 rounded-2xl font-bold hover:bg-amber-800 transition-all shadow-xl">Rolü Oluştur</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminRoles;
